import type { GetWorkspaceCreditSummaryQuery } from '@graphql/queries/credit-summary/core.graphql';

export type CreditPlanKind = 'CREDIT_PACKS' | 'UNLIMITED' | 'PAYG';

export type CreditSummary = GetWorkspaceCreditSummaryQuery['workspaceCreditSummary'];

/** Everything the credit UI reads off a summary, independent of the query lifecycle. */
export type CreditSummaryDerivation = {
  summary: CreditSummary | null;
  planKind: CreditPlanKind;
  /** Σ granted credits over the workspace's *live* finite grants. */
  purchasedMilli: number;
  /** Σ consumed credits over the workspace's *live* finite grants. */
  consumedFromPacksMilli: number;
  /** Spendable credits right now, straight from the ledger's own end balance. */
  availableMilli: number;
  renewalDate: string | null;
};

export type CreditSummaryView = CreditSummaryDerivation & {
  loading: boolean;
  /** The query errored. Distinguishes a failure from a summary still in flight. */
  failed: boolean;
  /** Runs the query again, for a consumer offering a retry. */
  retry: () => void;
};

const ADJUSTMENT_GRANT_TYPE = 'ADJUSTMENT';

const EMPTY_DERIVATION: CreditSummaryDerivation = {
  summary: null,
  planKind: 'PAYG',
  purchasedMilli: 0,
  consumedFromPacksMilli: 0,
  availableMilli: 0,
  renewalDate: null,
};

/**
 * Reduce a credit summary to the figures the dropdown row, overlay hero and plan
 * card render. Pure so the arithmetic can be tested without React.
 *
 * `summary.grants` only ever carries grants that are still live this month — the
 * ledger's summary already excludes any bucket that has fully expired, reporting
 * what remains available via `remainingMilli` and what that live remainder
 * projects to lose at month end via `expiringThisMonthMilli`. So every aggregate
 * below counts all finite grants in `summary.grants`, with no expiry filter.
 */
export function deriveCreditSummaryView(
  summary: CreditSummary | null,
  nowMilliseconds: number,
): CreditSummaryDerivation {
  if (!summary) {
    return EMPTY_DERIVATION;
  }

  const liveFiniteGrants = summary.grants.filter((grant) => !grant.isUnlimited);
  const hasUnlimitedGrant = summary.grants.some((grant) => grant.isUnlimited);

  // A workspace with no finite grants left in the summary — its packs have
  // actually expired, not merely projected to — has no balance left to meter,
  // so its month reads as pay as you go rather than as a drained credit pack.
  let planKind: CreditPlanKind = 'PAYG';
  if (hasUnlimitedGrant) {
    planKind = 'UNLIMITED';
  } else if (liveFiniteGrants.length > 0) {
    planKind = 'CREDIT_PACKS';
  }

  const purchasedMilli = liveFiniteGrants.reduce(
    (total, grant) => total + grant.grantedCreditsMilli,
    0,
  );
  const consumedFromPacksMilli = liveFiniteGrants.reduce(
    (total, grant) => total + grant.consumedCreditsMilli,
    0,
  );

  const upcomingRenewalDates = summary.grants
    .filter((grant) => grant.grantType !== ADJUSTMENT_GRANT_TYPE)
    .map((grant) => grant.effectiveTo)
    .filter((effectiveTo): effectiveTo is string => effectiveTo != null)
    .filter((effectiveTo) => new Date(effectiveTo).getTime() > nowMilliseconds)
    .sort(
      (earlierDate, laterDate) => new Date(earlierDate).getTime() - new Date(laterDate).getTime(),
    );

  return {
    summary,
    planKind,
    purchasedMilli,
    consumedFromPacksMilli,
    // The ledger publishes its own Σ of live finite remainders, so the balance is
    // taken rather than reconstructed from purchased − consumed.
    availableMilli: summary.balanceMilli,
    renewalDate: upcomingRenewalDates[0] ?? null,
  };
}
